import React, { ChangeEvent, useState } from 'react';

import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Stack,
  Typography,
} from '@mui/material';

import { useProgressPolling } from '~/components/Accommodation/hooks/useProgressPolling';
import Spinner from '~/components/Common/Spinner';

import { ProgressStatusResponse, getProgressStatuses } from '~/services/AccommodationService';
import { HttpResponse } from '~/services/types';

import ProgressStateItem from '../Accommodation/Pages/Properties/components/ProgressStateItem';

export interface InitiateContentGenerationRequest {
  contentType?: 'description' | 'nearby' | 'finePrint';
  includeOfferInGeneration?: boolean;
  useWebSearch: boolean;
}

interface Props {
  onClose: () => void;
  initiateContentGeneration: (
    params: InitiateContentGenerationRequest,
  ) => Promise<HttpResponse<ProgressStatusResponse>>;
  fetchContent: () => void;
  includeOfferInGeneration?: boolean;
  setUseWebSearch?: (useWebSearch: boolean) => void;
  useWebSearch: boolean;
  contentType?: 'description' | 'nearby' | 'finePrint';
}

export default function AccommodationPropertyGenerateContentModal({
  onClose,
  initiateContentGeneration,
  fetchContent,
  includeOfferInGeneration,
  useWebSearch,
  setUseWebSearch,
  contentType,
}: Props) {
  const [isInitialConfirm, setIsInitialConfirm] = useState(true);

  const handleUseWebSearch = (e: ChangeEvent<HTMLInputElement>) => {
    setUseWebSearch?.(e.target.checked);
  };

  const { isLoading, isComplete, statusMessage, errorMessage, statuses, runStartJob, resetAndClose } =
    useProgressPolling<ProgressStatusResponse>({
      apiCall: async () => {
        const response = await initiateContentGeneration({
          contentType,
          includeOfferInGeneration,
          useWebSearch,
        });
        return {
          status: response.status,
          message: response.message,
          result: { id: response.result.id || '' },
        } as HttpResponse<{ id: string }>;
      },
      getStatus: getProgressStatuses,
      onClose,
    });

  const contentGeneration = statuses.find((status) => status.type === 'generatePropertyContent');

  const handleClose = () => {
    resetAndClose();
    onClose();
  };

  const fetchContentAndClose = () => {
    fetchContent();
    handleClose();
  };

  if (isInitialConfirm) {
    return (
      <Dialog open fullWidth maxWidth="lg">
        <DialogTitle>{contentType ? `Generate ${contentType} content` : 'Generate Content'}</DialogTitle>
        <DialogContent>
          <Box my={2}>
            <Typography>
              This action will initiate the generation of base content. This is a long-running task that may take
              several minutes to complete. This view will refresh automatically as the job progresses.
            </Typography>
            <Stack spacing={2} mt={2}>
              <Typography variant="body2" color="text.secondary">
                The generation prompt is configured in the OpenAI Dashboard and cannot be changed here.
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={useWebSearch}
                    onChange={handleUseWebSearch}
                    inputProps={{ 'aria-label': 'Web Search Toggle' }}
                  />
                }
                label="Use Web Search (Beta)"
              />
            </Stack>
            <Typography mt={2}>Are you sure you want to proceed?</Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="text">
            Cancel
          </Button>
          <Button
            onClick={() => {
              setIsInitialConfirm(false);
              runStartJob();
            }}
            variant="contained"
          >
            Start Generation
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open onClose={handleClose}>
      <DialogTitle>Generating Content</DialogTitle>
      <DialogContent sx={{ minWidth: '400px' }}>
        <Typography mb={2}>
          This is a long-running task that may take several minutes to complete. This view will refresh automatically as
          the job progresses. Please do not close this view.
        </Typography>
        <Stack spacing={2}>
          {contentGeneration && <ProgressStateItem label={contentGeneration.message} state={contentGeneration.state} />}
          {isComplete && statusMessage && <DialogContentText color="primary">{statusMessage}</DialogContentText>}
          {errorMessage && <DialogContentText color="error">{errorMessage}</DialogContentText>}
        </Stack>
      </DialogContent>
      {!isComplete && (
        <DialogActions>
          <Button variant="text" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          {isLoading && (
            <Button variant="contained" disabled>
              <Spinner size={15} inline />
            </Button>
          )}
        </DialogActions>
      )}
      {isComplete && (
        <DialogActions>
          <Button variant="contained" onClick={fetchContentAndClose}>
            View Content
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
